import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { Field, Form, Formik } from 'formik';
import { equals } from 'ramda';
import { Button } from '@daedalus/atlas/Button';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { useToastController } from '@daedalus/atlas/context/toastController';
import { Divider } from '@daedalus/atlas/Divider';
import { InputText } from '@daedalus/atlas/experimental/InputText';
import { InputWrapperFormik } from '@daedalus/atlas/helpers/InputWrapperFormik';
import { Icon } from '@daedalus/atlas/Icon';
import { Label } from '@daedalus/atlas/Label';
import { Link } from '@daedalus/atlas/Link';
import { Text } from '@daedalus/atlas/Text';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity, Team } from '@daedalus/core/src/analytics/types/Events';
import { getCognitoUserAttributes, getUserProfileUpdatePending as getCognitoUserProfileUpdatePending } from '@daedalus/core/src/auth/modules/selectors';
import { updateUserAttributes } from '@daedalus/core/src/auth/modules/thunks';
import { User } from '@daedalus/core/src/auth/types/Cognito';
import { Brand } from '@daedalus/core/src/brand/types';
import { checkIsBrandVio, getLocalizedContactUrl } from '@daedalus/core/src/brand/utils';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { getLanguageCode } from '@daedalus/core/src/localization/services/locale';
import { toastMessages } from '@daedalus/shared/src/user/account/constants/notificationMessages';
import { DeleteUserProfile } from '../DeleteUserProfile';
const ACCOUNT_FORM_ID = 'account-form-id';

// Used to trigger the form submission when the user clicks the save button in the top bar
export const TopBarSubmitButton = () => <Button size="md" type="submit" variant="quiet" form={ACCOUNT_FORM_ID}>
    <Text variant="labelM">
      <FormattedMessageWrapper id="preferencesSaveButton" defaultMessage="Save" />
    </Text>
  </Button>;
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "w1hcyi5u",
  propsAsIs: false
});
const _exp5 = /*#__PURE__*/() => Form;
export const FormExtended = /*#__PURE__*/styled(_exp5())({
  name: "FormExtended",
  class: "f1mz7ul1",
  propsAsIs: true
});
const FormWrapper = /*#__PURE__*/styled('div')({
  name: "FormWrapper",
  class: "f1uza79r",
  propsAsIs: false
});

/**
 * Wrapper component to change the appearance of the disabled text input, to make it look like it's not disabled,
 * it applies custom styles to the inner component.
 */
const InputTextStyledWrapper = /*#__PURE__*/styled('div')({
  name: "InputTextStyledWrapper",
  class: "iz9rerp",
  propsAsIs: false
});
const _exp13 = /*#__PURE__*/() => InputText;
const DisabledInputText = /*#__PURE__*/styled(_exp13())({
  name: "DisabledInputText",
  class: "dv8y4em",
  propsAsIs: true
});
const _exp18 = /*#__PURE__*/() => Link;
const StyledLink = /*#__PURE__*/styled(_exp18())({
  name: "StyledLink",
  class: "simbz1b",
  propsAsIs: true
});
const DeleteAccountWrapper = /*#__PURE__*/styled('div')({
  name: "DeleteAccountWrapper",
  class: "d18v2mgy",
  propsAsIs: false
});
const getToastContent = (brand: Brand, languageCode: string) => <FormattedMessageWrapper id="account.emailNotEditable" defaultMessage="We don’t support editing email address at this point. If you need help please <ContactSupport></ContactSupport>" values={{
  ContactSupport: () => <StyledLink href={getLocalizedContactUrl(brand, languageCode)} target="_blank" rel="noreferrer">
          <FormattedMessageWrapper id="contactSupport" defaultMessage="contact support" />
        </StyledLink>
}} />;
const VerifiedBadgeStyles = "vq6rpr0";
interface PersonalDetailsFormPropTypes {
  brand: Brand;
}
export const PersonalDetailsForm = ({
  brand
}: PersonalDetailsFormPropTypes) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const userAttributes = useSelector(getCognitoUserAttributes);
  const languageCode = useSelector(getLanguageCode);
  const user = {
    given_name: userAttributes?.given_name,
    family_name: userAttributes?.family_name,
    phone_number: userAttributes?.phone_number,
    picture: userAttributes?.picture,
    email: userAttributes?.email
  };
  const isVio = checkIsBrandVio(brand);
  const toast = useToastController();
  const onEmailClicked = () => {
    toast.open(getToastContent(brand, languageCode));
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.AccountEmail,
      action: Action.Clicked,
      team: Team.Retention
    }));
  };
  const isSubmitting = useSelector(getCognitoUserProfileUpdatePending);
  const {
    isMobile
  } = useDeviceLayout();
  const onSubmit = useCallback(async (user: User['attributes']) => {
    try {
      await dispatch(updateUserAttributes({
        given_name: user?.given_name,
        family_name: user?.family_name,
        phone_number: user?.phone_number,
        picture: user?.picture,
        email: user?.email
      }));
      toast.open(<FormattedMessageWrapper {...toastMessages.userDataSaved} />, {
        icon: <Icon name="Checkmark" />
      });
    } catch (error) {
      toast.open(<FormattedMessageWrapper {...toastMessages.userDataSaveError} />, {
        icon: <Icon name="FaceSad" />
      });
    }
  }, [dispatch]);
  const handleSubmit = useCallback((formData: User['attributes']) => {
    const isSameData = equals([formData.given_name, formData.family_name], [user.given_name, user.family_name]);
    if (!isSameData) onSubmit(formData);
  }, [onSubmit, user]);
  return <Wrapper>
      {!isMobile ? <>
          <Text variant="titleXL">
            <FormattedMessageWrapper id="account" defaultMessage="Account" />
          </Text>

          <Divider />

          <Text variant="titleL">
            <FormattedMessageWrapper id="accountContent.personalInfo" defaultMessage="Personal info" />
          </Text>
        </> : null}

      {(user?.phone_number || user?.email) && <Formik initialValues={user} onSubmit={handleSubmit}>
          <FormExtended id={ACCOUNT_FORM_ID}>
            <FormWrapper>
              <Label forId="email" variant="bodyS" value={<FormattedMessageWrapper id="firstName" defaultMessage="First name" />}>
                <InputWrapperFormik Field={Field} name="given_name">
                  <InputText isPrivate id="given_name" dataId="AccountFirstNameField" name="given_name" isValid placeholder={intl.formatMessage({
                id: 'firstName',
                defaultMessage: 'First name'
              })} />
                </InputWrapperFormik>
              </Label>

              <Label forId="lastName" variant="bodyS" value={<FormattedMessageWrapper id="lastName" defaultMessage="Last name" />}>
                <InputWrapperFormik Field={Field} name="family_name">
                  <InputText isPrivate id="family_name" name="family_name" dataId="AccountLastNameField" isValid placeholder={intl.formatMessage({
                id: 'lastName',
                defaultMessage: 'Last name'
              })} />
                </InputWrapperFormik>
              </Label>

              {user.email && <Label forId="email" variant="bodyS" isRequired value={<FormattedMessageWrapper id="emailAddress" defaultMessage="Email address" />}>
                  <InputTextStyledWrapper onClick={onEmailClicked}>
                    <DisabledInputText isPrivate disabled id="email" name="email" dataId="AccountEmailField" value={user.email} suffix={<div className={VerifiedBadgeStyles}>
                          <Icon size="sm" name="Checkmark" />
                          <FormattedMessage id="InputText.verified" defaultMessage="Verified" />
                        </div>} placeholder={intl.formatMessage({
                id: 'emailAddress',
                defaultMessage: 'Email address'
              })} />
                  </InputTextStyledWrapper>
                </Label>}

              {user.phone_number && <Label forId="phone_number" variant="bodyS" isRequired value={<FormattedMessageWrapper id="phoneNumber" defaultMessage="Phone number" />}>
                  <DisabledInputText isPrivate disabled id="phone_number" name="phone_number" dataId="AccountPhoneField" value={user.phone_number} suffix={<div className={VerifiedBadgeStyles}>
                        <Icon size="sm" name="Checkmark" />
                        <FormattedMessage id="InputText.verified" defaultMessage="Verified" />
                      </div>} placeholder={intl.formatMessage({
              id: 'phoneNumber',
              defaultMessage: 'Phone number'
            })} />
                </Label>}
            </FormWrapper>

            {!isMobile && <Button fullWidth dataId="UpdateAccountDetailsCTA" type="submit" size="lg" loading={isSubmitting} disabled={isSubmitting}>
                <FormattedMessageWrapper id="updatePersonalDetails" defaultMessage="Update personal details" />
              </Button>}
          </FormExtended>
        </Formik>}

      {!isMobile ? <>
          <Text variant="titleL">
            <FormattedMessageWrapper id="accountContent.deleteAccount" defaultMessage="Delete account" />
          </Text>

          <DeleteAccountWrapper>
            <Text variant="bodyM">
              {isVio ? <FormattedMessageWrapper id="permanentlyDeleteAccount" defaultMessage="Permanently delete my Vio.com account" /> : <FormattedMessageWrapper id="membership.permanentlyDeleteAccount" defaultMessage="Permanently delete my account" />}
            </Text>

            <DeleteUserProfile />
          </DeleteAccountWrapper>
        </> : null}
    </Wrapper>;
};

require("./index.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");